/**
 * Super Simple EventStream Hook
 * Just connect and log - nothing fancy
 */

import { useEffect, useRef, useState } from 'react';
import { auth } from '../config/firebase';
import { logger } from '../utils/logger';

interface EventStreamOptions {
  clientId?: string;
  eventTypes?: string[];
  onEvent?: (eventData: any) => void;
}

export const useEventStreamSimple = (options: EventStreamOptions = {}) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');

  // Keep a ref to the latest onEvent callback so the SSE handler always uses the current closure
  const onEventRef = useRef(options.onEvent);
  onEventRef.current = options.onEvent;

  useEffect(() => {
    // cancelled flag prevents leaked connections in React StrictMode.
    // StrictMode unmounts before the async getIdToken() resolves, so
    // cleanup can't close an EventSource that doesn't exist yet. Without
    // this flag, the first mount's connect() creates an orphaned connection
    // after the second mount has already started its own.
    let cancelled = false;
    mountedRef.current = true;

    const connect = async () => {
      logger.log('🔌 [SimpleEventStream] Starting connection...');

      try {
        // Get token
        const user = auth.currentUser;
        if (!user) {
          logger.log('❌ [SimpleEventStream] No user logged in');
          return;
        }

        const token = await user.getIdToken();

        // Bail out if this effect instance was cancelled during the await
        if (cancelled) {
          logger.log('🛑 [SimpleEventStream] Connection cancelled (stale effect)');
          return;
        }

        logger.log('✅ [SimpleEventStream] Got token');

        // Build URL with dynamic client ID
        const clientIdParam = options.clientId || 'xyz-corp'; // Fallback to xyz-corp if not provided
        const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
        const eventTypesParam = (options.eventTypes || ['gmail_processed']).join(',');
        const url = `${baseUrl}/api/v1/events/stream?token=${token}&client_id=${clientIdParam}&event_types=${eventTypesParam}`;
        logger.log('🔍 [SimpleEventStream] Using client_id:', clientIdParam);
        logger.log('🌐 [SimpleEventStream] Connecting to SSE endpoint');

        // Create EventSource
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          logger.log('✅ [SimpleEventStream] Connected!');
          setStatus('connected');
          reconnectAttemptsRef.current = 0; // Reset on successful connection
        };

        eventSource.onmessage = (event) => {
          logger.log('📨 [SimpleEventStream] Message received:', event.data);
          
          try {
            const data = JSON.parse(event.data);
            
            // Handle different message types
            if (data.type === 'event') {
              logger.log('🎯 [SimpleEventStream] Event received:', data.data.type, data.data.title);
              
              // Call custom event handler if provided (use ref for latest closure)
              if (onEventRef.current) {
                onEventRef.current(data.data);
              }
            } else if (data.type === 'connection') {
              logger.log('🔗 [SimpleEventStream] Connection confirmed');
            } else if (data.type === 'heartbeat') {
              logger.log('💓 [SimpleEventStream] Heartbeat');
            }
          } catch (error) {
            logger.log('❌ [SimpleEventStream] Error parsing message:', error);
          }
        };

        eventSource.onerror = (error) => {
          logger.log('❌ [SimpleEventStream] Error:', error);
          setStatus('error');
          eventSource.close();
          
          // Reconnect with exponential backoff: 5s, 10s, 20s, 30s (max), up to 10 attempts
          const MAX_ATTEMPTS = 10;
          const attempts = reconnectAttemptsRef.current;
          if (attempts < MAX_ATTEMPTS && mountedRef.current && !cancelled) {
            reconnectAttemptsRef.current = attempts + 1;
            const delay = Math.min(5000 * Math.pow(2, attempts), 30000);
            logger.log(`🔄 [SimpleEventStream] Reconnecting in ${delay / 1000}s (attempt ${attempts + 1}/${MAX_ATTEMPTS})...`);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current && !cancelled) {
                setStatus('connecting');
                connect();
              }
            }, delay);
          } else {
            logger.log('❌ [SimpleEventStream] Max reconnection attempts reached or component unmounted');
          }
        };

      } catch (error) {
        logger.log('💥 [SimpleEventStream] Connection failed:', error);
        setStatus('error');
      }
    };

    connect();

    return () => {
      logger.log('🧹 [SimpleEventStream] Cleanup');
      cancelled = true;
      mountedRef.current = false;
      
      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Close EventSource
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []); // Only run once

  return { status };
};

export default useEventStreamSimple;